/**
 * Leads data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, which is
 * loaded only in dev / VITE_FIXTURES builds. Owned by the leads feature. Shared entity reads (jobs, techs, customers,
 * vehicles, catalog) live in core/entities.ts.
 *
 * Paths: leads = P1 (`/v1/app/leads*`, P2 §3.5); click-to-call, calls and recordings = P4 §3.5; the SMS thread is the
 * P2 §3.5 conversations path (P4 owns SMS).
 */
import { API_BASE, APP, ApiError, type Http, type ReqOpts } from '@app/core/http';
import type { Call, ISO, Lead, Message, Page, UUID } from '@app/core/types';
import { apiCall, apiCustomer, apiLead, notAvailable } from '@app/core/live/dto';
import { dispositionOf } from './dispositions';

export type LeadQueue = 'new' | 'requests' | 'mine' | 'followups' | 'ai' | 'all';
export const LEAD_QUEUES: { key: LeadQueue; label: string }[] = [
  { key: 'new', label: 'New' }, { key: 'requests', label: 'Booking requests' }, { key: 'mine', label: 'Mine' }, { key: 'followups', label: 'Follow-ups due' },
  { key: 'ai', label: 'AI calls' }, { key: 'all', label: 'All' },
];
/** Sub-filter under "All" (spam/vendor never show in New; S2.11). */
export type LeadFilter = '' | 'open' | 'booked' | 'lost' | 'spam' | 'vendor';
export const LEAD_FILTERS: { key: LeadFilter; label: string }[] = [
  { key: '', label: 'Everything' }, { key: 'open', label: 'Open' }, { key: 'booked', label: 'Booked' },
  { key: 'lost', label: 'Lost' }, { key: 'spam', label: 'Spam' }, { key: 'vendor', label: 'Vendor' },
];

/** The API lead row, plus the optional email a web-form lead carries. */
export type LeadRow = Lead & { email?: string | null };

export interface LeadQuery { queue: LeadQueue; filter?: LeadFilter; q?: string; after?: string; limit?: number }
export type LeadCounts = Record<Exclude<LeadQueue, 'all'>, number>;
/** POST …/claim: atomic claim-lock (P1). `won:false` → someone else holds a live lease. */
export interface ClaimResult { won: boolean; owner_id: UUID | null; owner_name: string | null; lease_expires_at: ISO | null }
export interface BridgeResult { call_id: UUID }
export interface LeadHistoryItem {
  id: string; at: ISO; actor_name: string | null; text: string;
  kind: 'created' | 'ai_call' | 'claimed' | 'released' | 'disposition' | 'follow_up' | 'call' | 'sms' | 'converted' | 'system';
}
export interface LeadCall { call: Call; recording_url: string | null }
export interface Conversation { customer_id: UUID; sms_consent: 'granted' | 'revoked' | 'unknown'; opted_out_at: ISO | null; messages: Message[] }

export interface Api {
  leads(q: LeadQuery): Promise<Page<LeadRow>>;
  counts(): Promise<LeadCounts>;
  lead(id: string): Promise<LeadRow>;
  history(id: string): Promise<LeadHistoryItem[]>;
  call(callId: string): Promise<LeadCall>;
  claim(id: string, o: { key: string; signal?: AbortSignal }): Promise<ClaimResult>;
  release(id: string, key: string): Promise<LeadRow>;
  disposition(id: string, body: { disposition: string; note?: string }, key: string): Promise<LeadRow>;
  followUp(id: string, dueAt: ISO, key: string): Promise<LeadRow>;
  clickToCall(leadId: string, key: string): Promise<BridgeResult>;
  conversation(customerId: string): Promise<Conversation>;
  sendSms(customerId: string, body: string, key: string): Promise<Message>;
}

const enc = encodeURIComponent;
type Row = Record<string, any>;
const OPEN = ['new', 'requested', 'contacted', 'quoted'];
/** Queue / filter → P1 `status=`/`source=`; the queue parts P1 can't filter (mine, follow-ups due) are narrowed here. */
function listQuery(q: LeadQuery) {
  switch (q.queue) {
    case 'new': return { status: 'new,requested' };
    case 'requests': return { status: 'requested' };
    case 'mine': case 'followups': return { status: OPEN.join(',') };
    case 'ai': return { source: 'ai_receptionist' };
    default: return q.filter === 'open' ? { status: OPEN.join(',') } : q.filter === 'booked' ? { status: 'booked' }
      : q.filter === 'lost' || q.filter === 'spam' || q.filter === 'vendor' ? { status: 'lost,dnc' } : {};
  }
}
function inQueue(l: LeadRow, q: LeadQuery, me: string) {
  const due = !!l.follow_up_due_at && Date.parse(l.follow_up_due_at) <= Date.now();
  switch (q.queue) {
    case 'new': return !l.claimed_by || l.claimed_by === me;
    case 'mine': return l.claimed_by === me;
    case 'followups': return due;
    default: return q.filter === 'spam' || q.filter === 'vendor' ? l.disposition === q.filter : true;
  }
}
/** Disposition → P1 lead status (P1 has status only; the disposition key travels in `reason` as `<key>[: note]`). */
function statusFor(key: string) {
  if (key === 'do_not_contact') return 'dnc';
  if (key === 'booked') return 'booked';
  return dispositionOf(key)?.lease === 'release' ? 'lost' : 'contacted';
}

/**
 * Live = P1 api/src/modules/leads (list/get/claim/release/status/convert) + P4 voice (calls, click-to-call).
 * Missing server pieces degrade here and are listed in the report: counts (computed from the open list), history
 * (synthesised from the row), follow-up scheduling + free-text SMS (not_available), SMS thread (empty).
 */
export const live = (http: Http): Api => {
  const names = new Map<string, string>();
  let meId: Promise<string> | null = null;
  const me = () => (meId ??= http.get<Row>(`${APP}/me`).then((m) => String(m.user?.id ?? '')).catch((e) => { meId = null; throw e; }));
  const get = (id: string) => http.get<{ lead: Row }>(`${APP}/leads/${enc(id)}`).then((r) => apiLead(r.lead, names));
  const list = (query: Record<string, unknown>) => http.get<{ items: Row[]; next: string | null }>(`${APP}/leads`, { query: query as ReqOpts['query'] });

  return {
    leads: async (q) => {
      const [r, uid] = await Promise.all([list({ ...listQuery(q), q: q.q, after: q.after, limit: q.limit }), me()]);
      const items = (r.items ?? []).map((x) => apiLead(x, names)).filter((l) => inQueue(l, q, uid));
      return { items, next: r.next ?? null };
    },
    counts: async () => {
      const [r, uid] = await Promise.all([list({ status: OPEN.join(','), limit: 200 }), me()]);
      const rows = (r.items ?? []).map((x) => apiLead(x, names));
      const n = (qq: LeadQueue) => rows.filter((l) => (qq === 'new' ? l.status === 'new' || l.status === 'requested' : true) && inQueue(l, { queue: qq }, uid)).length;
      return { new: n('new'), requests: rows.filter((l) => l.status === 'requested').length, mine: n('mine'), followups: n('followups'),
        ai: rows.filter((l) => l.source === 'ai_receptionist').length };
    },
    lead: get,
    history: async (id) => {
      const l = await get(id);
      const h: LeadHistoryItem[] = [{ id: `${id}-created`, at: l.created_at, kind: 'created', actor_name: null, text: `Lead created (${l.source.replace(/_/g, ' ')})` }];
      if (l.claimed_at) h.push({ id: `${id}-claimed`, at: l.claimed_at, kind: 'claimed', actor_name: l.claimed_by_name, text: 'Claimed' });
      if (l.disposition) h.push({ id: `${id}-disp`, at: l.last_contact_at ?? l.created_at, kind: 'disposition', actor_name: null, text: `Marked ${l.disposition.replace(/_/g, ' ')}` });
      if (l.job_id) h.push({ id: `${id}-job`, at: l.last_contact_at ?? l.created_at, kind: 'converted', actor_name: null, text: 'Booked as a job' });
      return h.sort((a, b) => b.at.localeCompare(a.at));
    },
    call: (callId) => http.get<{ call: Row; turns?: Row[]; recordings?: Row[] }>(`${APP}/calls/${enc(callId)}`).then((r) => {
      const call = apiCall(r.call, r.turns ?? [], r.recordings ?? []);
      return { call, recording_url: call.recording_id ? `${API_BASE}${APP}/calls/${enc(call.id)}/recording` : null };
    }),
    claim: (id, o) => http.post<{ won: boolean; owner: string | null; lease_expires_at: string | null }>(`${APP}/leads/${enc(id)}/claim`, {}, { idempotencyKey: o.key, signal: o.signal })
      .then((r) => ({ won: !!r.won, owner_id: r.owner ?? null, owner_name: (r.owner && names.get(r.owner)) ?? null, lease_expires_at: r.lease_expires_at ? new Date(r.lease_expires_at).toISOString() : null })),
    release: (id, key) => http.post(`${APP}/leads/${enc(id)}/release`, {}, { idempotencyKey: key }).then(() => get(id)),
    disposition: async (id, body, key) => {
      const d = dispositionOf(body.disposition);
      const note = body.note?.trim();
      // The server does not auto-claim or release on a status change: renew my lease first, release after closing.
      if (d?.lease === 'renew') await http.post(`${APP}/leads/${enc(id)}/claim`, {}, { idempotencyKey: `${key}:claim` });
      await http.post(`${APP}/leads/${enc(id)}/status`, { status: statusFor(body.disposition), reason: (note ? `${body.disposition}: ${note}` : body.disposition).slice(0, 300) }, { idempotencyKey: key });
      if (d?.lease === 'release') await http.post(`${APP}/leads/${enc(id)}/release`, {}, { idempotencyKey: `${key}:release` }).catch((e) => { if (!(e instanceof ApiError && e.code === 'not_claimer')) throw e; });
      return get(id);
    },
    followUp: () => notAvailable('lead_follow_up'),
    clickToCall: (lead_id, key) => http.post<{ call_id: string }>(`${APP}/calls/click-to-call`, { lead_id }, { idempotencyKey: key }).then((r) => ({ call_id: r.call_id })),
    // No staff conversation route yet (P4): consent from the customer record, no thread.
    conversation: (cid) => http.get<{ customer: Row }>(`${APP}/customers/${enc(cid)}`).then((r) => {
      const c = apiCustomer(r.customer);
      return { customer_id: cid, sms_consent: c.sms_consent, opted_out_at: null, messages: [] };
    }),
    // P4 sends templated SMS only (POST /v1/app/sms/send refuses free text): free-form replies are rolling out.
    sendSms: () => notAvailable('free_text_sms'),
  };
};
